/**
 * Zod schemas for the entire site configuration.
 *
 * These schemas are the single source of truth for the configuration shape: every
 * TypeScript type in this module is *inferred* from a schema, so a schema change can
 * never silently drift away from its type.
 *
 * Two conventions run through the file:
 *
 * 1. `z.input<...>` is what a configuration file authors. Fields with defaults are
 *    optional there, which keeps `config/*.ts` terse.
 *    `z.output<...>` is what the application consumes, defaults applied, arrays
 *    normalised, nothing optional that has a default.
 *
 *    A default that depends on a sibling field cannot be expressed as a Zod default, so
 *    `mapSchema` applies it with a `.transform()`. That is the one place in the file
 *    where the input shape still allows `undefined` and the output shape does not.
 *
 * 2. Objects are `.strict()`. A typo in a configuration file is a bug, and the
 *    validator's job is to name it rather than to ignore it.
 */
import { z } from 'zod';

/* -------------------------------------------------------------------------- */
/* Shared primitives                                                          */
/* -------------------------------------------------------------------------- */

/**
 * Ids are the internal identity of a thing: DOM ids, object keys, the value a map is
 * referenced by. They are not URL segments any more (slugs are, see `slugSchema`), so
 * they keep the wider alphabet and no id ever needs escaping.
 */
const idSchema = (label: string) =>
	z
		.string()
		.trim()
		.min(1, `${label} is required`)
		.regex(
			/^[a-z0-9][a-z0-9._-]*$/,
			`${label} must be lowercase and URL-safe (a-z, 0-9, ".", "_", "-")`
		);

/**
 * Slugs are the URL segment of a map (`/<slug>/`), served from the domain root, so
 * they are stricter than ids: hyphens only.
 *
 * The leading-alphanumeric rule is what makes `/_bluemap/` (the BlueMap proxy) and
 * `/_app/` (SvelteKit's assets) structurally unclaimable, and it also rules out `.`
 * and `..` as slugs. Banning `.` goes further, keeping a slug from shadowing a root
 * static file such as `favicon.svg` — with it, no reserved-name list is needed.
 */
const SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;

/**
 * Exported because a slug can be *derived* rather than authored (see the `mapSchema`
 * transform), and a derived value never passes through this schema. The validator tests
 * the effective slug with this, so the pattern has one definition.
 */
export const isValidSlug = (value: string): boolean => SLUG_PATTERN.test(value);

const slugSchema = (label: string) =>
	z
		.string()
		.trim()
		.min(1, `${label} is required`)
		.regex(
			SLUG_PATTERN,
			`${label} must be lowercase, hyphens only, and start and end with a letter or digit`
		);

function isHttpUrl(value: string): boolean {
	try {
		const { protocol } = new URL(value);
		return protocol === 'http:' || protocol === 'https:';
	} catch {
		return false;
	}
}

/** Absolute http(s) URL. Hand-rolled so the message names the offending field. */
const httpUrlSchema = (label: string) =>
	z.string().trim().refine(isHttpUrl, `${label} must be an absolute http(s) URL`);

/** Non-empty, trimmed display string. */
const labelSchema = (label: string) => z.string().trim().min(1, `${label} is required`);

/** Icon reference: either a lucide-svelte export name, or an image URL. */
const iconSchema = labelSchema('icon');

/* -------------------------------------------------------------------------- */
/* UI feature flags                                                           */
/* -------------------------------------------------------------------------- */

/**
 * The complete set of features that can be switched off by configuration.
 *
 * Components must ask this list, never a hardcoded literal, whether to mount a
 * feature. Adding a key here makes it available to the global UI config, to
 * per-map overrides, and to type checking in one step.
 */
export const UI_FEATURE_KEYS = [
	'sidebar',
	'mapSelector',
	'search',
	'coordinates',
	'compass',
	'zoomControls',
	'orientationControls',
	'fullscreen',
	'playerList',
	'markerPanel',
	'mobileControls',
	'dimensionSwitcher',
	'minimapMode'
] as const;

export type UiFeatureKey = (typeof UI_FEATURE_KEYS)[number];

/**
 * Site-wide defaults. `satisfies` makes this exhaustive: forgetting a feature key
 * is a compile error, not a silently missing flag.
 */
export const UI_FEATURE_DEFAULTS = {
	sidebar: true,
	mapSelector: true,
	search: true,
	coordinates: true,
	compass: true,
	zoomControls: true,
	orientationControls: true,
	fullscreen: true,
	playerList: true,
	markerPanel: true,
	mobileControls: true,
	dimensionSwitcher: true,
	minimapMode: false
} satisfies Record<UiFeatureKey, boolean>;

/**
 * The schema for every feature flag.
 *
 * Defaults are declared per key rather than on the object, so a configuration file
 * may set one flag and inherit the rest. `UI_FEATURE_DEFAULTS` is checked for
 * exhaustiveness with `satisfies`, so adding a feature to `UI_FEATURE_KEYS` without
 * adding it here is a compile error rather than a flag that silently reads as
 * `undefined`.
 */
export const uiFeaturesSchema = z
	.object({
		sidebar: z.boolean().default(UI_FEATURE_DEFAULTS.sidebar),
		mapSelector: z.boolean().default(UI_FEATURE_DEFAULTS.mapSelector),
		search: z.boolean().default(UI_FEATURE_DEFAULTS.search),
		coordinates: z.boolean().default(UI_FEATURE_DEFAULTS.coordinates),
		compass: z.boolean().default(UI_FEATURE_DEFAULTS.compass),
		zoomControls: z.boolean().default(UI_FEATURE_DEFAULTS.zoomControls),
		orientationControls: z.boolean().default(UI_FEATURE_DEFAULTS.orientationControls),
		fullscreen: z.boolean().default(UI_FEATURE_DEFAULTS.fullscreen),
		playerList: z.boolean().default(UI_FEATURE_DEFAULTS.playerList),
		markerPanel: z.boolean().default(UI_FEATURE_DEFAULTS.markerPanel),
		mobileControls: z.boolean().default(UI_FEATURE_DEFAULTS.mobileControls),
		dimensionSwitcher: z.boolean().default(UI_FEATURE_DEFAULTS.dimensionSwitcher),
		minimapMode: z.boolean().default(UI_FEATURE_DEFAULTS.minimapMode)
	})
	.strict();

export type ResolvedFeatures = z.output<typeof uiFeaturesSchema>;

/**
 * Per-map overrides: the same keys, every one optional, and deliberately **no
 * defaults**.
 *
 * `.partial()` is what removes them. An absent key has to stay absent so that
 * `resolveFeatures` can tell "not specified for this map" apart from "explicitly
 * switched off here", with a default applied, those two would be identical and a
 * map could no longer inherit a change to the site-wide setting.
 */
export const featureOverridesSchema = uiFeaturesSchema.partial();
export type FeatureOverrides = z.output<typeof featureOverridesSchema>;

/* -------------------------------------------------------------------------- */
/* BlueMap targeting                                                          */
/* -------------------------------------------------------------------------- */

/**
 * Points a map at a BlueMap instance.
 *
 * `server` references an entry in `config/servers.ts` and is the preferred form , 
 * it keeps origins in one place and lets an environment variable supply the URL.
 * `baseUrl` is the escape hatch for a one-off map on its own instance.
 */
export const bluemapTargetSchema = z
	.object({
		server: idSchema('bluemap.server').optional(),
		baseUrl: httpUrlSchema('bluemap.baseUrl').optional(),
		mapId: idSchema('bluemap.mapId').describe("BlueMap's own map id."),
		worldId: idSchema('bluemap.worldId').optional()
	})
	.strict()
	.refine((target) => Boolean(target.server ?? target.baseUrl), {
		message: 'bluemap needs either `server` (recommended) or an explicit `baseUrl`'
	});

export type BlueMapTargetInput = z.input<typeof bluemapTargetSchema>;

/* -------------------------------------------------------------------------- */
/* Maps                                                                       */
/* -------------------------------------------------------------------------- */

/** Initial camera for a map, mirroring BlueMap's own start-view fields. */
export const startViewSchema = z
	.object({
		position: z
			.object({
				x: z.number().finite(),
				/** Optional: a 2D marker or flat start view has no meaningful height. */
				y: z.number().finite().optional(),
				z: z.number().finite()
			})
			.strict()
			.optional(),
		rotation: z
			.object({
				yaw: z.number().finite(),
				pitch: z.number().finite(),
				distance: z.number().positive().optional()
			})
			.strict()
			.optional(),
		zoom: z.number().finite().optional()
	})
	.strict();

export const mapSchema = z
	.object({
		id: idSchema('map id'),

		/**
		 * URL segment, i.e. `/<slug>/`. Optional when authored: it defaults to `id`, which
		 * is applied by the transform below rather than here, because a Zod default cannot
		 * read a sibling field. Doing it in the schema is what lets the validator check the
		 * very values the router uses.
		 */
		slug: slugSchema('map slug').optional(),

		name: labelSchema('map name'),
		description: labelSchema('map description').optional(),
		icon: iconSchema.optional(),

		/** Grouping key for the map selector. Categories are implicit: any string works. */
		category: idSchema('map category').optional(),

		/** `false` removes the map from the site entirely. */
		enabled: z.boolean().default(true),
		/** `false` keeps the map reachable by URL but out of the selector. */
		visible: z.boolean().default(true),
		/** `false` renders the map read-only: no selector entry, no URL route. */
		selectable: z.boolean().default(true),
		/** Marks the fallback map. At most one enabled map may set this. */
		default: z.boolean().default(false),

		order: z.number().finite().default(0),

		/** Links sibling maps that are dimensions of one world. */
		group: idSchema('map group').optional(),
		/** Free-form dimension label: overworld, nether, end, or a modded id. */
		dimension: labelSchema('map dimension').optional(),

		/** Free-text tags, matched by the map search. */
		tags: z.array(labelSchema('tag')).default([]),

		bluemap: bluemapTargetSchema,

		/** Narrows the global UI features for this map only. */
		features: featureOverridesSchema.optional(),

		startView: startViewSchema.optional()
	})
	.strict()
	/**
	 * Order matters: `.strict()` rejects unknown keys at parse time, and the pipe this
	 * returns has no `.strict()` of its own, so it must come first.
	 *
	 * The effect on the types is the point: `MapConfigInput` (z.input) keeps `slug`
	 * optional so authoring stays terse, while `MapConfig` (z.output) has it required, so
	 * nothing downstream has to ask whether a map has one.
	 */
	.transform((map) => ({ ...map, slug: map.slug ?? map.id }));

export type MapConfigInput = z.input<typeof mapSchema>;
export type MapConfig = z.output<typeof mapSchema>;

/* -------------------------------------------------------------------------- */
/* Map categories                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Display metadata for map grouping.
 *
 * Categories are declared rather than inferred from their id so that the selector
 * can show a human name, an icon and a stable order without a component ever
 * hardcoding one. A map referencing an undeclared category is a configuration
 * error, reported by `validate.ts`.
 */
export const mapCategorySchema = z
	.object({
		id: idSchema('map category id'),
		name: labelSchema('map category name'),
		icon: iconSchema.optional(),
		order: z.number().finite().default(0)
	})
	.strict();

export type MapCategoryInput = z.input<typeof mapCategorySchema>;
export type MapCategory = z.output<typeof mapCategorySchema>;

/* -------------------------------------------------------------------------- */
/* Marker categories                                                          */
/* -------------------------------------------------------------------------- */

export const MARKER_SOURCES = ['bluemap', 'api'] as const;

export const markerCategorySchema = z
	.object({
		id: idSchema('marker category id'),
		name: labelSchema('marker category name'),
		description: labelSchema('marker category description').optional(),
		icon: iconSchema.optional(),
		/** Accent colour for this category; falls back to the theme accent. */
		color: labelSchema('marker category colour').optional(),

		enabled: z.boolean().default(true),
		visibleByDefault: z.boolean().default(true),

		/**
		 * BlueMap marker-set id backing this category. Required when `source` is
		 * `bluemap`, because BlueMap's own marker sets are the only authoritative
		 * source of marker data.
		 */
		bluemapSetId: labelSchema('bluemapSetId').optional(),

		/** Where this category's markers come from. */
		source: z.enum(MARKER_SOURCES).default('bluemap'),

		order: z.number().finite().default(0)
	})
	.strict()
	.refine((category) => category.source !== 'bluemap' || Boolean(category.bluemapSetId), {
		message: "a `bluemap` marker category needs `bluemapSetId`",
		path: ['bluemapSetId']
	});

export type MarkerCategoryInput = z.input<typeof markerCategorySchema>;
export type MarkerCategory = z.output<typeof markerCategorySchema>;

/* -------------------------------------------------------------------------- */
/* Players                                                                    */
/* -------------------------------------------------------------------------- */

export const PLAYER_SOURCES = ['bluemap-live', 'api', 'none'] as const;
export type PlayerSourceKind = (typeof PLAYER_SOURCES)[number];

export const PLAYER_SOURCE_DEFAULTS = {
	kind: 'bluemap-live',
	pollIntervalMs: 5_000
} as const satisfies z.input<typeof playerSourceSchema>;

export const playerSourceSchema = z
	.object({
		/**
		 * Where live player positions come from.
		 * - `bluemap-live`: the live player feed served by the BlueMap webserver.
		 * - `api`: a Minecraft plugin API (not yet present) via `endpoint`.
		 * - `none`: player overlay is disabled regardless of the flags below.
		 */
		kind: z.enum(PLAYER_SOURCES).default('bluemap-live'),

		/**
		 * Endpoint template for `api` sources. `{mapId}` and `{worldId}` are
		 * substituted per map; relative paths resolve against `PUBLIC_API_BASE_URL`.
		 */
		endpoint: z
			.string()
			.trim()
			.min(1, 'endpoint is required')
			.describe('Template supporting {mapId} and {worldId} placeholders.')
			.optional(),

		pollIntervalMs: z.number().int().positive().default(5_000)
	})
	.strict()
	.refine((source) => source.kind !== 'api' || Boolean(source.endpoint), {
		message: "player source `api` needs an `endpoint` template",
		path: ['endpoint']
	});

export type PlayerSource = z.input<typeof playerSourceSchema>;
export type ResolvedPlayerSource = z.output<typeof playerSourceSchema>;

/**
 * Used when a configuration omits `source` entirely.
 *
 * Zod types the whole `source` block as optional because a configuration file may
 * leave it out, but leaving it out must not mean "undefined everywhere". Resolving it
 * once here means no consumer needs optional chaining, and `kind: 'none'` is the
 * honest reading of "nobody said where players come from".
 */
export const PLAYER_SOURCE_FALLBACK: ResolvedPlayerSource = {
	kind: 'none',
	pollIntervalMs: 5_000
};

export const playerOverlaySchema = z
	.object({
		enabled: z.boolean().default(true),
		showNames: z.boolean().default(true),
		showHeads: z.boolean().default(true),
		showCoordinates: z.boolean().default(false),
		searchable: z.boolean().default(true),
		trackable: z.boolean().default(true),
		/** Pause polling while the tab is hidden, to spare the server. */
		pauseWhenHidden: z.boolean().default(true),
		source: playerSourceSchema.default(PLAYER_SOURCE_DEFAULTS)
	})
	.strict();

export type PlayerOverlayConfig = z.input<typeof playerOverlaySchema>;
export type ResolvedPlayerOverlayConfig = z.output<typeof playerOverlaySchema>;

/* -------------------------------------------------------------------------- */
/* BlueMap servers                                                            */
/* -------------------------------------------------------------------------- */

export const bluemapServerSchema = z
	.object({
		id: idSchema('server id'),
		name: labelSchema('server name'),
		description: labelSchema('server description').optional(),

		/** Origin of the BlueMap webserver serving this instance's data. */
		baseUrl: httpUrlSchema('server baseUrl'),

		/** BlueMap's own web app, for "open in BlueMap" escapes. */
		webappUrl: httpUrlSchema('server webappUrl').optional(),

		/** Marks the fallback server for maps that do not name one. */
		default: z.boolean().default(false)
	})
	.strict();

export type BlueMapServerInput = z.input<typeof bluemapServerSchema>;
export type BlueMapServer = z.output<typeof bluemapServerSchema>;

/* -------------------------------------------------------------------------- */
/* Site, theme and UI layout                                                  */
/* -------------------------------------------------------------------------- */

export const COLOR_SCHEMES = ['dark', 'light', 'system'] as const;
export type ColorScheme = (typeof COLOR_SCHEMES)[number];

/**
 * One colour palette.
 *
 * Both the dark and the light palette share this shape, which is what lets the
 * theme toggle be a swap of custom-property values rather than a second stylesheet.
 */
export const paletteSchema = z
	.object({
		accent: labelSchema('palette.accent'),
		/** Text colour placed on top of `accent`. */
		accentContrast: labelSchema('palette.accentContrast'),

		background: labelSchema('palette.background'),
		surface: labelSchema('palette.surface'),
		surfaceMuted: labelSchema('palette.surfaceMuted'),
		border: labelSchema('palette.border'),

		text: labelSchema('palette.text'),
		textMuted: labelSchema('palette.textMuted'),

		danger: labelSchema('palette.danger'),
		success: labelSchema('palette.success'),
		warning: labelSchema('palette.warning')
	})
	.strict();

export type Palette = z.output<typeof paletteSchema>;

/** Default dark palette: neutral surfaces so the world stays the colourful part. */
export const DARK_PALETTE: Palette = {
	accent: '#4ec5d4',
	accentContrast: '#04191d',
	background: '#0b0e13',
	surface: '#12161d',
	surfaceMuted: '#1a1f28',
	border: '#252c37',
	text: '#e7eaf0',
	textMuted: '#98a2b3',
	danger: '#e5534b',
	success: '#3fb950',
	warning: '#d29922'
};

export const LIGHT_PALETTE: Palette = {
	accent: '#0e7c8c',
	accentContrast: '#ffffff',
	background: '#eef1f5',
	surface: '#ffffff',
	surfaceMuted: '#f4f6f9',
	border: '#d8dee7',
	text: '#111823',
	textMuted: '#5b6675',
	danger: '#c9332b',
	success: '#1a7f37',
	warning: '#9a6700'
};

/**
 * Branding tokens. Components never hardcode a colour; they read the CSS custom
 * properties these generate (see `$lib/theme/applyTheme.ts`), so re-skinning the
 * site, or shipping a new palette, is a config edit.
 */
export const themeSchema = z
	.object({
		/** The scheme to start in. `system` follows the OS preference and stays live. */
		colorScheme: z.enum(COLOR_SCHEMES).default('dark'),
		/** Whether the visitor may switch schemes at runtime. */
		switchable: z.boolean().default(true),

		dark: paletteSchema.default(DARK_PALETTE),
		light: paletteSchema.default(LIGHT_PALETTE),

		radius: labelSchema('theme.radius').default('0.75rem'),
		fontSans: labelSchema('theme.fontSans').default(
			"'IBM Plex Sans', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif"
		),
		fontMono: labelSchema('theme.fontMono').default(
			"'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace"
		)
	})
	.strict();

export type ThemeConfig = z.input<typeof themeSchema>;
export type ResolvedThemeConfig = z.output<typeof themeSchema>;

export const siteLinkSchema = z
	.object({
		label: labelSchema('link label'),
		href: httpUrlSchema('link href'),
		icon: iconSchema.optional()
	})
	.strict();

export const siteSchema = z
	.object({
		name: labelSchema('site.name'),
		tagline: labelSchema('site.tagline').optional(),
		logo: iconSchema.optional(),
		links: z.array(siteLinkSchema).default([]),
		theme: themeSchema
	})
	.strict();

export type SiteConfig = z.input<typeof siteSchema>;
export type ResolvedSiteConfig = z.output<typeof siteSchema>;

export const SIDEBAR_SIDES = ['left', 'right'] as const;
export const MOBILE_PRESENTATIONS = ['drawer-left', 'drawer-right', 'bottom-sheet'] as const;

export const UI_LAYOUT_DEFAULTS = {
	sidebarSide: 'left',
	sidebarWidth: '20rem',
	mobilePresentation: 'bottom-sheet',
	mapFullBleed: true
} as const satisfies z.input<typeof uiLayoutSchema>;

export const uiLayoutSchema = z
	.object({
		sidebarSide: z.enum(SIDEBAR_SIDES).default('left'),
		/** CSS length for the desktop sidebar. */
		sidebarWidth: labelSchema('ui.layout.sidebarWidth').default('20rem'),
		mobilePresentation: z.enum(MOBILE_PRESENTATIONS).default('bottom-sheet'),
		/** Edge-to-edge map: no page chrome competing with the world. */
		mapFullBleed: z.boolean().default(true)
	})
	.strict();

export const UI_MAP_SELECTOR_DEFAULTS = {
	searchThreshold: 6,
	showCategories: true,
	showDescriptions: true,
	showIcons: true,
	layout: 'list'
} as const;

export const uiMapSelectorSchema = z
	.object({
		/** Search only appears once there are more selectable maps than this. */
		searchThreshold: z.number().int().nonnegative().default(6),
		showCategories: z.boolean().default(true),
		showDescriptions: z.boolean().default(true),
		showIcons: z.boolean().default(true),
		layout: z.enum(['list', 'grid']).default('list')
	})
	.strict();

export const UI_SEARCH_DEFAULTS = {
	coordinates: true,
	markers: true,
	players: true,
	placeholder: 'Search maps, markers, players or coordinates…',
	debounceMs: 200
} as const;

export const uiSearchSchema = z
	.object({
		/** Coordinate parsing is entirely client-side and always available. */
		coordinates: z.boolean().default(true),
		markers: z.boolean().default(true),
		players: z.boolean().default(true),
		placeholder: labelSchema('ui.search.placeholder').default(
			'Search maps, markers, players or coordinates…'
		),
		debounceMs: z.number().int().nonnegative().default(200)
	})
	.strict();

export const uiConfigSchema = z
	.object({
		/** Master feature switches. Components must consult these, not literals. */
		features: uiFeaturesSchema,
		layout: uiLayoutSchema.default(UI_LAYOUT_DEFAULTS),
		mapSelector: uiMapSelectorSchema.default(UI_MAP_SELECTOR_DEFAULTS),
		search: uiSearchSchema.default(UI_SEARCH_DEFAULTS)
	})
	.strict();

export type UiConfigInput = z.input<typeof uiConfigSchema>;
export type ResolvedUiConfig = z.output<typeof uiConfigSchema>;

/* -------------------------------------------------------------------------- */
/* Root configuration                                                         */
/* -------------------------------------------------------------------------- */

/**
 * The raw configuration, as authored across `config/*.ts`.
 *
 * Note that these are `z.input` types: configuration files may omit anything that
 * has a default. `validate.ts` turns a `RawConfig` into a `ResolvedConfig`, which
 * is what the rest of the application imports.
 */
export const rawConfigSchema = z
	.object({
		site: siteSchema,
		servers: z.array(bluemapServerSchema).min(1, 'at least one BlueMap server is required'),
		mapCategories: z.array(mapCategorySchema).default([]),
		maps: z.array(mapSchema),
		markerCategories: z.array(markerCategorySchema).default([]),
		players: playerOverlaySchema,
		ui: uiConfigSchema
	})
	.strict();

export type RawConfig = z.input<typeof rawConfigSchema>;
export type ResolvedConfig = z.output<typeof rawConfigSchema>;
